import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import Header from "@/components/Header";
import HomePage from "@/pages/HomePage";
import PropertiesPage from "@/pages/PropertiesPage";
import PropertyDetailPage from "@/pages/PropertyDetailPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ListPropertyPage from "@/pages/ListPropertyPage";
import EditPropertyPage from "@/pages/EditPropertyPage";
import DashboardPage from "@/pages/DashboardPage";
import FavoritesPage from "@/pages/FavoritesPage";
import PricingPage from "@/pages/PricingPage";
import PaymentReturnPage from "@/pages/PaymentReturnPage";
import AdminVerificationsPage from "@/pages/AdminVerificationsPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import BoostPage from "@/pages/BoostPage";
import ReelsPage from "@/pages/ReelsPage";
import LegalPage from "@/pages/LegalPage";
import CityLandingPage from "@/pages/CityLandingPage";
import LocalityLandingPage from "@/pages/LocalityLandingPage";
import BlogPage from "@/pages/BlogPage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import ReferPage from "@/pages/ReferPage";
import OwnersLandingPage from "@/pages/OwnersLandingPage";
import StaffShell from "@/pages/staff/StaffShell";
import StaffLoginPage from "@/pages/staff/StaffLoginPage";
import StaffDashboardPage from "@/pages/staff/StaffDashboardPage";
import StaffTeamPage from "@/pages/staff/StaffTeamPage";
import UnsubscribeSearchPage from "@/pages/UnsubscribeSearchPage";
import AssistantPage from "@/pages/AssistantPage";
import InstallPWAPrompt from "@/components/InstallPWAPrompt";
import AiSearchLauncher from "@/components/AiSearchLauncher";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="p-16 text-center text-gray-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/**
 * Global referral capture — any page load with ?ref=CODE in the URL stashes
 * the code in localStorage so it can be redeemed later, whether the user
 * signs up via email/password (RegisterPage) or Google Auth (auth.jsx).
 *
 * Before this hook, the ref code was only captured when the visitor landed
 * DIRECTLY on /register?ref=CODE. Anyone who arrived via /?ref=CODE,
 * /property/xyz?ref=CODE, /rent/bengaluru?ref=CODE etc. lost the code, and
 * the referrer never got credited when the visitor later signed up.
 */
function useReferralCapture() {
  const loc = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const ref = params.get("ref");
    if (!ref) return;
    const clean = ref.trim().toUpperCase().slice(0, 32);
    if (!/^[A-Z0-9-]+$/.test(clean)) return;
    try { window.localStorage.setItem("tlr_ref", clean); } catch {}
  }, [loc.search]);
}

function Shell() {
  useReferralCapture();
  const loc = useLocation();
  // /staff/* is a completely separate portal with its own layout and no
  // customer header/footer. We branch here so a single BrowserRouter still
  // serves both experiences without any header flicker.
  const isStaffRoute = loc.pathname.startsWith("/staff");

  if (isStaffRoute) {
    return (
      <div className="App">
        <Routes>
          <Route path="/staff/login" element={<StaffLoginPage />} />
          <Route path="/staff" element={<StaffShell />}>
            <Route index element={<StaffDashboardPage />} />
            <Route path="team" element={<StaffTeamPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/staff" replace />} />
        </Routes>
        <Toaster position="top-right" richColors theme="dark" />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/properties" element={<PropertiesPage />} />
        <Route path="/assistant" element={<AssistantPage />} />
        <Route path="/discover" element={<ReelsPage />} />
        <Route path="/reels" element={<ReelsPage />} />
        <Route path="/legal/:doc" element={<LegalPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/rent/:city/:locality" element={<LocalityLandingPage />} />
        <Route path="/rent/:city" element={<CityLandingPage />} />
        <Route path="/sale/:city" element={<CityLandingPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPage />} />
        <Route path="/property/:id" element={<PropertyDetailPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/refer" element={<ReferPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/unsubscribe-search/:token" element={<UnsubscribeSearchPage />} />
        <Route path="/list-your-property" element={<OwnersLandingPage />} />
        <Route path="/owners" element={<OwnersLandingPage />} />
        <Route path="/list-property" element={<ListPropertyPage />} />
        <Route path="/edit-property/:id" element={<Protected><EditPropertyPage /></Protected>} />
        <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
        <Route path="/favorites" element={<Protected><FavoritesPage /></Protected>} />
        <Route path="/admin/verifications" element={<Protected><AdminVerificationsPage /></Protected>} />
        <Route path="/admin" element={<Protected><AdminDashboardPage /></Protected>} />
        <Route path="/boost" element={<Protected><BoostPage /></Protected>} />
        <Route path="/payment-return" element={<PaymentReturnPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!loc.pathname.startsWith("/assistant") && (
      <footer className="mt-24 border-t border-[#EFE3D0] bg-[#FBF6EC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-8">
            <div className="md:col-span-2">
              <p className="font-display text-2xl text-[#7A1E2A] mb-2">To-Let Rentals</p>
              <p className="text-xs text-gray-500 leading-relaxed">
                Rent or buy homes directly from verified owners across India. Zero brokerage. Free forever.
              </p>
            </div>
            <div>
              <p className="label-eyebrow text-gray-500 mb-3">Popular cities</p>
              <ul className="space-y-2 text-sm">
                {[
                  ["bengaluru", "Rent in Bengaluru"],
                  ["mumbai", "Rent in Mumbai"],
                  ["delhi", "Rent in Delhi"],
                  ["pune", "Rent in Pune"],
                  ["hyderabad", "Rent in Hyderabad"],
                  ["chennai", "Rent in Chennai"],
                  ["gurgaon", "Rent in Gurgaon"],
                  ["jodhpur", "Rent in Jodhpur"],
                ].map(([k, label]) => (
                  <li key={k}>
                    <Link to={`/rent/${k}`} data-testid={`footer-city-${k}`} className="text-gray-700 hover:text-[#7A1E2A]">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="label-eyebrow text-gray-500 mb-3">Explore</p>
              <ul className="space-y-2 text-sm">
                <li><Link to="/properties" className="text-gray-700 hover:text-[#7A1E2A]">Browse properties</Link></li>
                <li><Link to="/discover" className="text-gray-700 hover:text-[#7A1E2A]">Discover · Scroll</Link></li>
                <li><Link to="/pricing" className="text-gray-700 hover:text-[#7A1E2A]">Pricing</Link></li>
                <li><Link to="/list-property" className="text-gray-700 hover:text-[#7A1E2A]">List your property</Link></li>
              </ul>
            </div>
            <div>
              <p className="label-eyebrow text-gray-500 mb-3">Legal &amp; Info</p>
              <ul className="space-y-2 text-sm">
                <li><Link to="/legal/terms" data-testid="footer-terms" className="text-gray-700 hover:text-[#7A1E2A]">Terms of Service</Link></li>
                <li><Link to="/legal/privacy" data-testid="footer-privacy" className="text-gray-700 hover:text-[#7A1E2A]">Privacy Policy</Link></li>
                <li><Link to="/legal/refund" data-testid="footer-refund" className="text-gray-700 hover:text-[#7A1E2A]">Refund Policy</Link></li>
                <li><Link to="/legal/cookies" data-testid="footer-cookies" className="text-gray-700 hover:text-[#7A1E2A]">Cookie Policy</Link></li>
                <li><Link to="/legal/guidelines" data-testid="footer-guidelines" className="text-gray-700 hover:text-[#7A1E2A]">Community Guidelines</Link></li>
                <li><Link to="/legal/about" data-testid="footer-about" className="text-gray-700 hover:text-[#7A1E2A]">About us</Link></li>
                <li><Link to="/legal/contact" data-testid="footer-contact" className="text-gray-700 hover:text-[#7A1E2A]">Contact &amp; Support</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-6 border-t border-[#EFE3D0] flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-500">© {new Date().getFullYear()} To-Let Rentals — Homes near you.</p>
            <p className="text-[10px] text-gray-400 tracking-widest uppercase">Made in India · For local landlords &amp; renters</p>
          </div>
        </div>
      </footer>
      )}
      <Toaster position="top-right" richColors />
      <InstallPWAPrompt />
      <AiSearchLauncher />
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}
